import { useEffect, useRef } from 'react';

/**
 * The behaviour every overlay needs, in one place.
 *
 * Modals and drawers get this wrong in the same four ways, so it is written once:
 *
 * - **Escape closes it.** Required by plan 9.5.
 * - **Focus moves inside on open, and back to the trigger on close.** Otherwise a
 *   keyboard user is returned to the top of the document and has to walk back down.
 * - **Tab is trapped.** Without it, tabbing leaves the dialog and lands on controls
 *   behind the scrim that cannot be seen.
 * - **The page behind does not scroll.** Scrolling a dialog to its end otherwise
 *   starts scrolling the page underneath it.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useOverlay(open: boolean, onClose: () => void) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onClose);

  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const container = containerRef.current;
    const focusable = container?.querySelectorAll<HTMLElement>(FOCUSABLE);
    // The first control, or the dialog itself when it holds none. Never nothing,
    // which would leave focus on the page behind the scrim.
    (focusable?.[0] ?? container)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeRef.current();
        return;
      }

      if (event.key !== 'Tab' || !containerRef.current) return;

      // Filtered on `hidden`, not on `offsetParent`. offsetParent is null for
      // anything inside a fixed-position container in some engines and is never
      // populated at all under jsdom, either of which turns the trap into a no-op
      // that looks like it works.
      const items = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((element) => !element.hasAttribute('hidden') && !element.closest('[hidden]'));
      const first = items.at(0);
      const last = items.at(-1);
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus();
    };
  }, [open]);

  return containerRef;
}
